/**
 * SearchInput Component
 * Global search input for the application
 */

import { useState } from 'react';
import { InputBase, IconButton, Paper } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

interface SearchInputProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  debounce?: number;
}

export default function SearchInput({
  placeholder = 'Buscar...',
  onSearch,
  debounce = 300,
}: SearchInputProps) {
  const [query, setQuery] = useState('');
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = event.target.value;
    setQuery(newQuery);

    // Clear previous timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Set new timeout for debounced search
    const newTimeoutId = setTimeout(() => {
      onSearch?.(newQuery);
    }, debounce);

    setTimeoutId(newTimeoutId);
  };

  const handleClear = () => {
    setQuery('');
    onSearch?.('');
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  return (
    <Paper
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        onSearch?.(query);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: (theme) => alpha(theme.palette.common.white, 0.15),
        '&:hover': {
          backgroundColor: (theme) => alpha(theme.palette.common.white, 0.25),
        },
        borderRadius: 2,
        px: 2,
        py: 0.5,
        minWidth: 0,
        width: '100%',
      }}
      elevation={0}
    >
      <SearchIcon sx={{ color: 'inherit', mr: 1, opacity: 0.7 }} />
      <InputBase
        placeholder={placeholder}
        value={query}
        onChange={handleChange}
        sx={{
          flex: 1,
          color: 'inherit',
          '& input': {
            padding: '4px 0',
          },
        }}
        inputProps={{ 'aria-label': 'search' }}
      />
      {query && (
        <IconButton
          size="small"
          onClick={handleClear}
          sx={{ color: 'inherit', opacity: 0.7 }}
          aria-label="limpiar búsqueda"
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
    </Paper>
  );
}
